import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, Mail, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--rts-blue-pale)] via-white to-[var(--rts-blue-light)]/40 grid place-items-center p-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <img src="/logo.jpg" alt="PARS logo" className="h-16 w-auto object-contain" />
        </Link>
        <div className="bg-white rounded-3xl shadow-xl border border-[var(--rts-blue-pale)] p-8">
          <h1 className="font-display text-2xl text-[var(--rts-ink)] mb-1">Admin sign in</h1>
          <p className="text-sm text-[var(--rts-muted)] mb-6">Access the PARS content dashboard.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <label className="block">
              <span className="block text-sm font-semibold text-[var(--rts-ink)] mb-1">Email</span>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rts-muted)]" />
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border-2 border-[var(--rts-blue-pale)] focus:border-[var(--rts-blue)] rounded-xl pl-10 pr-3 py-3 outline-none min-h-[44px]"
                  data-testid="login-email"
                />
              </div>
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-[var(--rts-ink)] mb-1">Password</span>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rts-muted)]" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border-2 border-[var(--rts-blue-pale)] focus:border-[var(--rts-blue)] rounded-xl pl-10 pr-3 py-3 outline-none min-h-[44px]"
                  data-testid="login-password"
                />
              </div>
            </label>
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--rts-blue-deep)] hover:bg-[var(--rts-blue)] text-white font-bold py-3 rounded-xl transition disabled:opacity-60 flex items-center justify-center gap-2 min-h-[44px]"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Sign in
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-[var(--rts-muted)]">
            <Link to="/" className="hover:underline">← Back to site</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
